/* Agatha redesign: collection page script (test theme). Owned by the collection builder.
 *
 * - ?ag_age=<ageId> (header age row, home height chart, ruler links) becomes the size filter of
 *   the current language (Agatha.ageQuery), keeping the other parameters.
 * - The age ruler highlights the age whose values are in the URL (or the remembered age) and applies
 *   an age in place through Dawn's FacetFiltersForm. The "taille" half of an age (products whose
 *   option is named Taille) is counted in the total and appended by "Show more" after the main list
 *   (#102, 1 Oct 2026; it used to be a secondary link).
 * - Links marked [data-ag-keep-filters] (type chips, Pour control) carry the current filter.* and
 *   sort_by parameters; option filters are dropped for a target where they match nothing.
 * - Desktop toolbar (active filters + sort above the grid), one pill for an age instead of one per
 *   size value, "Show more products" that appends the next page.
 * Dawn's facets.js replaces #ProductGridContainer and the facets on every filter change; everything
 * here is re-applied after each of those updates.
 * Fix pass: the search results page runs the same code (its head comes from the same section, F05);
 * chip counts are fetched sparingly (G2); the second size filter is renamed only when the main one is
 * also listed (T15).
 */
(() => {
  'use strict';

  const SIZE = 'filter.v.option.size';
  const TAILLE = 'filter.v.option.taille';
  const OPT = [SIZE, TAILLE];
  const AGE_IDS = ['naissance', '6-mois', '1-an', '2-ans', '4-ans', '6-ans', '8-ans', '10-ans', '12-ans'];
  const VALUE_CAP = 100; // a storefront filter lists at most 100 values

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const params = () => new URLSearchParams(location.search);
  const strip = (q) => String(q || '').replace(/^[?&]+/, '');

  let I = {};
  let A = null; // window.Agatha once loaded
  let ageSets = null; // {ageId: {size: Set, taille: Set, q}} from Agatha.ageQuery
  let rulerScrolled = false;
  // #102: the other half of the active age (the same age under the other option name, usually
  // "Taille"). Shopify ANDs two option filters and Collective suppliers own their option names, so
  // those products are counted in the total and appended by "Show more" after the main list.
  const noExtra = () => ({ url: '', n: 0, page: 0, done: true });
  let extra = noExtra();

  function whenAgatha(timeout = 5000) {
    return new Promise((resolve) => {
      if (window.Agatha) return resolve(window.Agatha);
      const t0 = Date.now();
      const iv = setInterval(() => {
        if (window.Agatha || Date.now() - t0 > timeout) {
          clearInterval(iv);
          resolve(window.Agatha || null);
        }
      }, 40);
    });
  }

  const fill = (s, v) => String(s || '').replace(/\{(\w+)\}/g, (_, k) => (v[k] !== undefined ? v[k] : ''));
  const fmt = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, I.sep || ',');
  const ageLabel = (id) => (I.ages && I.ages[id]) || id;

  /** Strings printed by the section (Liquid's t filter HTML-escapes them: decode once). */
  function readI18n() {
    const el = $('[data-ag-collection-i18n]');
    if (!el) return {};
    const box = document.createElement('textarea');
    const decode = (v) => {
      if (typeof v === 'string') {
        box.innerHTML = v;
        return box.value;
      }
      if (v && typeof v === 'object') {
        Object.keys(v).forEach((k) => {
          v[k] = decode(v[k]);
        });
      }
      return v;
    };
    try {
      return decode(JSON.parse(el.textContent));
    } catch (e) {
      return {};
    }
  }

  // ---------- counts ----------
  // Each count is a full filtered render on Shopify's side (G2): at most 3 at a time, one request per
  // URL (the sort order does not change a count, so it is left out of the key), and remembered for the
  // tab for a few minutes so going back and forth between chips does not ask again.
  const countCache = new Map();
  const queue = [];
  let running = 0;
  const TAB_TTL = 5 * 60 * 1000;
  function pump() {
    while (running < 3 && queue.length) {
      const job = queue.shift();
      running++;
      job.run().finally(() => {
        running--;
        pump();
      });
    }
  }
  /** Forget the queued (not yet sent) counts of one kind, e.g. the chips of a filter state the visitor
   *  has already left. Their promises resolve to null. */
  function dropQueued(tag) {
    for (let i = queue.length - 1; i >= 0; i--) {
      if (queue[i].tag === tag) queue.splice(i, 1)[0].cancel();
    }
  }
  function countKey(url) {
    const u = new URL(url, location.origin);
    ['sort_by', 'page', 'sections', 'section_id'].forEach((k) => u.searchParams.delete(k));
    return u.pathname + u.search;
  }
  function tabGet(key) {
    try {
      const v = JSON.parse(sessionStorage.getItem(`agatha-count:availability-v1:${key}`) || 'null');
      return v && Date.now() - v.t < TAB_TTL && typeof v.n === 'number' ? v.n : null;
    } catch (e) {
      return null;
    }
  }
  function tabSet(key, n) {
    try {
      sessionStorage.setItem(`agatha-count:availability-v1:${key}`, JSON.stringify({ n, t: Date.now() }));
    } catch (e) {
      /* storage full or blocked */
    }
  }
  /** products_count of any collection URL with its filters, from sections/agatha-count.liquid
   *  (Section Rendering API). Same contract as Agatha.count; kept here so the page does not
   *  depend on the request headers core uses (an Accept: application/json header makes Shopify
   *  answer with the collection JSON instead of the sections). 3 at a time, cached. */
  function fetchCount(path) {
    const u = new URL(path, location.origin);
    u.searchParams.delete('page');
    u.searchParams.delete('section_id');
    u.searchParams.set('sections', 'agatha-count');
    u.search = window.AgathaAvailability.params(u.searchParams, u.pathname).toString();
    return fetch(u.pathname + u.search, { credentials: 'same-origin' })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => {
        const m = String((j && j['agatha-count']) || '').match(/data-ag-count[^>]*>\s*(\d+)/);
        if (!m) throw new Error('agatha-count missing');
        return Number(m[1]);
      });
  }

  function countOf(url, tag = '') {
    const key = countKey(url);
    if (countCache.has(key)) return countCache.get(key);
    const saved = tabGet(key);
    if (saved !== null) {
      const hit = Promise.resolve(saved);
      countCache.set(key, hit);
      return hit;
    }
    const p = new Promise((resolve) => {
      queue.push({
        tag,
        run: () =>
          fetchCount(key)
            .then((n) => {
              tabSet(key, n);
              resolve(n);
            })
            .catch(() => {
              countCache.delete(key);
              resolve(null);
            }),
        cancel: () => {
          countCache.delete(key);
          resolve(null);
        },
      });
      pump();
    });
    countCache.set(key, p);
    return p;
  }

  /** products_count of the current view, as Dawn printed it in #ProductCount ("293 produits sur 462"). */
  function readCount() {
    if (document.getElementById('ProductGridContainer')?.dataset.agAvailabilityFallback === 'true') return null;
    const el = document.getElementById('ProductCount') || document.getElementById('ProductCountDesktop');
    if (!el) return null;
    const m = el.textContent.replace(/[\s  ,]/g, '').match(/\d+/);
    return m ? Number(m[0]) : null;
  }

  function renderCount() {
    const el = $('[data-ag-live-count]');
    const base = readCount();
    if (!el || base === null || !I.count_other) return;
    const n = base + extra.n;
    el.textContent = fill(n === 1 ? I.count_one : I.count_other, { n: fmt(n) });
  }

  // ---------- ages ----------
  async function loadAgeSets() {
    if (ageSets) return ageSets;
    const out = {};
    if (!A || typeof A.ageQuery !== 'function') return (ageSets = out);
    await Promise.all(
      AGE_IDS.map(async (id) => {
        try {
          const q = (await A.ageQuery(id)) || {};
          out[id] = {
            size: new Set(new URLSearchParams(strip(q.size)).getAll(SIZE)),
            taille: new Set(new URLSearchParams(strip(q.taille)).getAll(TAILLE)),
            q,
          };
        } catch (e) {
          /* unknown age: leave it out */
        }
      })
    );
    return (ageSets = out);
  }

  /** Which age the URL's size (or taille) values stand for, if any. An age is active when every
   *  value in the URL belongs to it and every value of it that this page lists is in the URL (so
   *  it survives Dawn's form, which re-sends only the listed checkboxes). */
  function detectAge(p) {
    for (const key of OPT) {
      const vals = p.getAll(key);
      if (!vals.length) continue;
      const cur = new Set(vals);
      const listed = new Set($$(`input[name="${key}"]`).map((i) => i.value));
      let best = null;
      let bestSize = Infinity;
      for (const id of AGE_IDS) {
        const set = ageSets && ageSets[id] && ageSets[id][key === SIZE ? 'size' : 'taille'];
        if (!set || !set.size) continue;
        if (!vals.every((v) => set.has(v))) continue;
        const expected = listed.size ? [...set].filter((v) => listed.has(v)) : [...set];
        if (!expected.length || !expected.every((v) => cur.has(v))) continue;
        if (set.size < bestSize) {
          best = id;
          bestSize = set.size;
        }
      }
      return { age: best, key };
    }
    return { age: null, key: null };
  }

  /** Ages that have at least one size value listed on this page, per filter (from the facet inputs). */
  function agesOnPage() {
    const found = { size: new Set(), taille: new Set(), capped: { size: false, taille: false }, any: false };
    const seen = { size: new Set(), taille: new Set() };
    let inputs = $$(`#FacetFiltersForm input[name="${SIZE}"], #FacetFiltersForm input[name="${TAILLE}"]`);
    if (!inputs.length) {
      inputs = $$(`#FacetFiltersFormMobile input[name="${SIZE}"], #FacetFiltersFormMobile input[name="${TAILLE}"]`);
    }
    let values = inputs.map((i) => ({ key: i.name === SIZE ? 'size' : 'taille', v: i.value, on: !i.disabled || i.checked }));
    if (!values.length) {
      // No facet form (filtering off): fall back to the labels printed by the section.
      try {
        const j = JSON.parse(($('[data-ag-age-values]') || {}).textContent || '{}');
        values = [
          ...(j.size || []).filter(Boolean).map((v) => ({ key: 'size', v, on: true })),
          ...(j.taille || []).filter(Boolean).map((v) => ({ key: 'taille', v, on: true })),
        ];
        found.capped.size = (j.sizeTotal || 0) >= VALUE_CAP;
        found.capped.taille = (j.tailleTotal || 0) >= VALUE_CAP;
      } catch (e) {
        /* ignore */
      }
    }
    values.forEach(({ key, v, on }) => {
      if (seen[key].has(v)) return;
      seen[key].add(v);
      if (!on) return;
      for (const id of AGE_IDS) {
        const set = ageSets && ageSets[id] && ageSets[id][key];
        if (set && set.has(v)) found[key].add(id);
      }
      if (A && typeof A.parseAge === 'function') {
        try {
          (A.parseAge(v) || []).forEach((id) => found[key].add(id));
        } catch (e) {
          /* ignore */
        }
      }
    });
    if (seen.size.size >= VALUE_CAP) found.capped.size = true;
    if (seen.taille.size >= VALUE_CAP) found.capped.taille = true;
    found.any = found.size.size + found.taille.size > 0;
    return found;
  }

  function urlWith(p) {
    const qs = p.toString();
    return location.pathname + (qs ? `?${qs}` : '');
  }

  function withoutOptions(p) {
    const q = new URLSearchParams(p);
    OPT.forEach((k) => q.delete(k));
    q.delete('page');
    q.delete('ag_age');
    return q;
  }

  function withAge(p, id, key) {
    const q = withoutOptions(p);
    const set = ageSets && ageSets[id];
    if (!set) return null;
    const src = key === TAILLE ? set.q.taille : set.q.size;
    if (!strip(src)) return null;
    new URLSearchParams(strip(src)).forEach((v, k) => q.append(k, v));
    return q;
  }

  /** Apply new query parameters: in place through Dawn's facets when possible, else navigate. */
  function go(p) {
    const qs = p.toString();
    // facets.js declares the class at top level (not on window); the custom element registry has it.
    const FFF = window.customElements && customElements.get('facet-filters-form');
    if (FFF && typeof FFF.renderPage === 'function' && document.getElementById('product-grid')) {
      FFF.renderPage(qs);
    } else {
      location.assign(urlWith(p));
    }
  }

  function keyFor(id, onPage) {
    if (onPage.size.has(id)) return SIZE;
    if (onPage.taille.has(id)) return TAILLE;
    const sizeListed = $$(`input[name="${SIZE}"]`).length > 0;
    const tailleListed = $$(`input[name="${TAILLE}"]`).length > 0;
    return !sizeListed && tailleListed ? TAILLE : SIZE;
  }

  /** ?ag_age=<id> -> the size filter of this language, keeping the other parameters. */
  async function convertAgeParam() {
    const p = params();
    if (!p.has('ag_age')) return false;
    const id = p.get('ag_age');
    const done = () => {
      document.documentElement.classList.remove('ag-age-pending');
      const q = new URLSearchParams(location.search);
      q.delete('ag_age');
      history.replaceState(history.state, '', urlWith(q) + location.hash);
    };
    if (!id || !AGE_IDS.includes(id) || !A) {
      done(); // unknown or empty (?ag_age= left by the language switch): just drop it
      return false;
    }
    await loadAgeSets();
    const q = withAge(p, id, keyFor(id, agesOnPage()));
    try {
      A.setAge(id);
    } catch (e) {
      /* storage blocked */
    }
    if (!q) {
      done();
      return false;
    }
    location.replace(urlWith(q) + location.hash);
    return true;
  }

  // ---------- ruler ----------
  let altToken = 0;
  // #142: on narrow screens the ruler scrolls sideways with its scrollbar hidden. Name the edges
  // that still hide ages (data-ag-more); agatha-collection.css fades them.
  function rulerEdges(ruler) {
    const sc = $('[data-ag-ruler-scroll]', ruler);
    if (!sc) return;
    const edges = () => {
      const max = sc.scrollWidth - sc.clientWidth;
      const more = [];
      if (max > 1 && sc.scrollLeft > 1) more.push('left');
      if (max > 1 && sc.scrollLeft < max - 1) more.push('right');
      if (more.length) sc.setAttribute('data-ag-more', more.join(' '));
      else sc.removeAttribute('data-ag-more');
    };
    sc.addEventListener('scroll', edges, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(edges).observe(sc);
    else window.addEventListener('resize', edges);
    edges();
  }
  async function renderRuler(p, active, onPage) {
    const ruler = $('[data-ag-ruler]');
    if (!ruler) return;
    const hasOpt = OPT.some((k) => p.has(k));
    const known = ageSets && Object.keys(ageSets).length > 0;
    if (!onPage.any && !hasOpt && known) {
      ruler.hidden = true;
      return;
    }
    ruler.hidden = false;
    let saved = null;
    try {
      saved = !hasOpt && A && typeof A.getAge === 'function' ? A.getAge() : null;
    } catch (e) {
      saved = null;
    }
    const unsure = onPage.capped.size || onPage.capped.taille || !known;
    $$('.ag-hmark', ruler).forEach((m) => {
      const id = m.dataset.agAge;
      const on = active.age === id;
      m.classList.toggle('is-on', on);
      if (on) m.setAttribute('aria-current', 'true');
      else m.removeAttribute('aria-current');
      const isSaved = !on && saved === id;
      m.classList.toggle('is-saved', isSaved);
      const s = $('[data-ag-age-saved]', m);
      if (s) s.hidden = !isSaved;
      const has = onPage.size.has(id) || onPage.taille.has(id);
      const empty = known && !has && !on;
      m.classList.toggle('is-empty', empty);
      if (empty && !unsure) m.setAttribute('aria-disabled', 'true');
      else m.removeAttribute('aria-disabled');
    });
    const all = $('[data-ag-age-all]', ruler);
    const hint = $('[data-ag-ruler-hint]', ruler);
    if (all) {
      all.hidden = !hasOpt;
      all.href = urlWith(withoutOptions(p));
    }
    if (hint) hint.hidden = hasOpt;

    const sc = $('[data-ag-ruler-scroll]', ruler);
    const focus = $('.ag-hmark.is-on', ruler) || $('.ag-hmark.is-saved', ruler);
    if (sc && focus && !rulerScrolled && sc.scrollWidth > sc.clientWidth) {
      sc.scrollLeft = Math.max(0, focus.offsetLeft - sc.clientWidth / 2);
      rulerScrolled = true;
    }

    // The other half of the age: the same age under the other option name.
    const alt = $('[data-ag-age-alt]', ruler);
    const token = ++altToken;
    if (alt) {
      alt.hidden = true;
      alt.textContent = '';
    }
    if (!active.age) return;
    const otherKey = active.key === SIZE ? TAILLE : SIZE;
    const q = withAge(p, active.age, otherKey);
    if (!q) return;
    const url = urlWith(q);
    const n = await countOf(url);
    if (token !== altToken || !n) return;
    // #102: one result per age. Count the other half and let "Show more" append it.
    extra = { url, n, page: 0, done: false };
    renderCount();
    setupMore();
  }

  function onRulerClick(e) {
    const mark = e.target.closest('.ag-hmark');
    const all = e.target.closest('[data-ag-age-all]');
    if (!mark && !all) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
    e.preventDefault();
    const p = params();
    if (all) {
      try {
        if (A) A.setAge(null);
      } catch (err) {
        /* ignore */
      }
      go(withoutOptions(p));
      return;
    }
    if (mark.getAttribute('aria-disabled') === 'true') return;
    const id = mark.dataset.agAge;
    if (!A || !ageSets || !ageSets[id]) {
      location.assign(mark.href);
      return;
    }
    if (detectAge(p).age === id) {
      go(withoutOptions(p)); // a second click on the chosen age clears it
      return;
    }
    const q = withAge(p, id, keyFor(id, agesOnPage()));
    try {
      A.setAge(id);
    } catch (err) {
      /* ignore */
    }
    if (q) go(q);
    else location.assign(mark.href);
  }

  // ---------- active filter pills ----------
  const closeSvg =
    '<svg aria-hidden="true" focusable="false" viewBox="0 0 12 12" width="12" height="12"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>';

  function collapseAgePills(p, active) {
    $$('.ag-age-pill').forEach((el) => el.remove());
    $$('.ag-pill-hidden').forEach((el) => el.classList.remove('ag-pill-hidden'));
    if (!active.age || !ageSets || !ageSets[active.age]) return;
    const set = ageSets[active.age][active.key === SIZE ? 'size' : 'taille'];
    $$('.active-facets-desktop, .active-facets-mobile').forEach((box) => {
      let first = null;
      $$('facet-remove', box).forEach((fr) => {
        const a = $('a[href]', fr);
        if (!a || fr.classList.contains('active-facets__button-wrapper')) return;
        let removed = null;
        try {
          const left = new Set(new URL(a.getAttribute('href'), location.origin).searchParams.getAll(active.key));
          removed = p.getAll(active.key).find((v) => !left.has(v));
        } catch (e) {
          removed = undefined;
        }
        if (removed !== undefined && set.has(removed)) {
          fr.classList.add('ag-pill-hidden');
          if (!first) first = fr;
        }
      });
      if (!first) return;
      const label = ageLabel(active.age);
      const pill = document.createElement('span');
      pill.className = 'ag-age-pill';
      const a = document.createElement('a');
      a.href = urlWith(withoutOptions(p));
      a.className = 'active-facets__button active-facets__button--light';
      a.innerHTML = `<span class="active-facets__button-inner button button--tertiary"><span class="ag-age-pill__text"></span><span class="svg-wrapper">${closeSvg}</span><span class="visually-hidden"></span></span>`;
      $('.ag-age-pill__text', a).textContent = label;
      $('.visually-hidden', a).textContent = fill(I.remove, { f: label });
      a.addEventListener('click', (e) => {
        e.preventDefault();
        go(withoutOptions(params()));
      });
      pill.append(a);
      first.before(pill);
    });
  }

  function relabelTaille() {
    // The store has two size filters ("Size" and "Taille" option names; in French both are labelled
    // "Taille"). Where both are listed, the main one comes first and the second one is called
    // "Taille (autre notation)". Where only the second one exists (a brand whose products all use
    // "Taille"), it keeps its own name: "other notation" would have nothing to be other than (T15).
    [
      ['#FacetsWrapperDesktop', 'Details-filter.v.option', false],
      ['#FacetsWrapperMobile', 'Details-Mobile-filter.v.option', true],
    ].forEach(([wrap, prefix, mobile]) => {
      const w = $(wrap);
      if (!w) return;
      const size = w.querySelector(`[id^="${prefix}.size-"]`);
      const taille = w.querySelector(`[id^="${prefix}.taille-"]`);
      if (!size || !taille) return;
      if (size.parentNode === taille.parentNode && size.compareDocumentPosition(taille) & Node.DOCUMENT_POSITION_PRECEDING) {
        size.after(taille);
      }
      if (!I.sizeOther) return;
      if (mobile) {
        const s = $('summary span', taille);
        if (s) s.textContent = I.sizeOther;
        const b = $('.mobile-facets__close-button span', taille);
        if (b) b.textContent = I.sizeOther;
      } else {
        const el = $('.facets__summary-label', taille);
        const t = el && Array.from(el.childNodes).find((n) => n.nodeType === 3 && n.textContent.trim());
        if (t) t.textContent = I.sizeOther;
      }
    });
    // Dawn's French file labels the price maximum "Destination" (a mistranslation of "To").
    if (I.priceTo) {
      $$('#main-collection-filters label.field__label[for$="-LTE"], #main-search-filters label.field__label[for$="-LTE"]').forEach((l) => {
        if (l.textContent.trim() !== I.priceTo) l.textContent = I.priceTo;
      });
    }
  }

  /** "2 ans (12)" -> "2 ans" + a right-aligned count, like the prototype's sidebar. */
  function decorateFacetCounts() {
    $$('.facet-checkbox__text[aria-hidden="true"]').forEach((el) => {
      if (el.dataset.agDone) return;
      el.dataset.agDone = '1';
      Array.from(el.childNodes).forEach((n) => {
        const m = n.nodeType === 3 && n.textContent.match(/^\s*\((\d+)\)\s*$/);
        if (!m) return;
        const small = document.createElement('small');
        small.className = 'ag-facet-count';
        small.textContent = fmt(Number(m[1]));
        n.replaceWith(small);
      });
    });
  }

  // ---------- links that keep the filters ----------
  function setChipCount(link, n) {
    const el = $('[data-ag-chip-count]', link) || $('small', link);
    if (!el) return;
    if (el.dataset.agBaseCount === undefined) el.dataset.agBaseCount = el.textContent;
    el.textContent = n === null ? el.dataset.agBaseCount : fmt(n);
  }

  // With a filter active, each chip counts its target inside the filters. Kept cheap (G2):
  // - the current collection's own chip and Pour link read the count already on the page;
  // - links with the same target (chip, Pour link, sidebar copy) share one request;
  // - the Pour "Tous" link shows no number and targets the union that holds this page's products, so
  //   it is not counted while this page has results (it is only counted to catch an empty target);
  // - a link is counted only once it is on screen (the phone chip row scrolls sideways, the sidebar
  //   list is hidden below 990px), and only when the browser is idle after the grid has been shown;
  // - counts still queued for a filter state the visitor has left are dropped.
  // Until its count arrives a link shows no number rather than the unfiltered one.
  let keepToken = 0;
  let keepObserver = null;
  const whenIdle = (fn) =>
    'requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 1500 }) : setTimeout(fn, 250);

  function isCurrentPage(u) {
    const here = params();
    return u.pathname === location.pathname && (u.searchParams.get('q') || '') === (here.get('q') || '');
  }

  function keepFilters(p) {
    const token = ++keepToken;
    if (keepObserver) {
      keepObserver.disconnect();
      keepObserver = null;
    }
    dropQueued('chips');
    const carry = [];
    p.forEach((v, k) => {
      if (k.startsWith('filter.') || k === 'sort_by') carry.push([k, v]);
    });
    const filtered = carry.some(([k]) => k.startsWith('filter.'));
    const hasOpt = carry.some(([k]) => OPT.includes(k));
    const groups = new Map(); // count key -> {full, dropped, self, links}
    $$('a[data-ag-keep-filters]').forEach((link) => {
      if (link.dataset.agBase === undefined) link.dataset.agBase = link.getAttribute('href') || '';
      let base;
      try {
        base = new URL(link.dataset.agBase, location.origin);
      } catch (e) {
        return;
      }
      const build = (pairs) => {
        const u = new URL(base.href);
        Array.from(new Set(u.searchParams.keys())).forEach((k) => {
          if (k.startsWith('filter.') || k === 'sort_by' || k === 'page') u.searchParams.delete(k);
        });
        pairs.forEach(([k, v]) => u.searchParams.append(k, v));
        return u.pathname + u.search + u.hash;
      };
      const full = build(carry);
      link.setAttribute('href', full);
      link.classList.remove('is-empty', 'is-counting');
      if (!filtered) {
        setChipCount(link, null);
        return;
      }
      const key = countKey(full);
      if (!groups.has(key)) {
        groups.set(key, {
          full,
          dropped: build(carry.filter(([k]) => !OPT.includes(k))),
          self: isCurrentPage(base),
          union: !!link.closest('.ag-pour') && !$('small', link),
          links: [],
        });
      }
      groups.get(key).links.push(link);
    });
    if (!groups.size) return;

    const apply = (g, n) => {
      if (token !== keepToken) return;
      g.links.forEach((link) => {
        link.classList.remove('is-counting');
        if (n === null) {
          const el = $('[data-ag-chip-count]', link) || $('small', link);
          if (el) el.textContent = ''; // unknown: say nothing rather than the unfiltered count
          return;
        }
        if (n === 0 && hasOpt) {
          // The option filter means nothing at the target (e.g. no sizes there): drop it for that link.
          link.setAttribute('href', g.dropped);
        }
        link.classList.toggle('is-empty', n === 0);
        setChipCount(link, n);
      });
    };
    const run = (g) => {
      if (g.started) return;
      g.started = true;
      const here = readCount();
      if (g.self || (g.union && here > 0)) apply(g, here);
      else countOf(g.full, 'chips').then((n) => apply(g, n));
    };
    groups.forEach((g) => {
      if (g.self || g.union) run(g);
      else g.links.forEach((link) => link.classList.add('is-counting'));
    });
    whenIdle(() => {
      if (token !== keepToken) return;
      if (!('IntersectionObserver' in window)) {
        groups.forEach(run);
        return;
      }
      const byLink = new Map();
      groups.forEach((g) => {
        if (!g.started) g.links.forEach((link) => byLink.set(link, g));
      });
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const g = e.isIntersecting && byLink.get(e.target);
          if (!g || token !== keepToken) return;
          run(g);
          g.links.forEach((link) => io.unobserve(link));
        });
      });
      byLink.forEach((_, link) => io.observe(link));
      keepObserver = io;
    });
  }

  // ---------- desktop toolbar: active filters + sort above the grid ----------
  function buildToolbar() {
    const grid = document.getElementById('ProductGridContainer');
    const vertical = grid && grid.closest('.facets-vertical');
    if (!vertical || $('.ag-toolbar')) return;
    const results = document.createElement('div');
    results.className = 'ag-results';
    const bar = document.createElement('div');
    bar.className = 'ag-toolbar';
    bar.id = 'AgToolbar';
    grid.parentNode.insertBefore(results, grid);
    results.append(bar, grid);
    const pills = $('#FacetFiltersForm .active-facets-desktop');
    if (pills) bar.append(pills);
    const sort = $('facet-filters-form.facets-vertical-sort');
    if (sort) bar.append(sort);
    document.documentElement.classList.add('ag-has-toolbar');
  }

  /** The prototype's sidebar opens with "Type d'article": the department's type links (same targets
   *  as the chips above, so they keep the filters too), shown as a radio list. Desktop only. */
  function buildTypeList() {
    const wrap = $('#FacetsWrapperDesktop');
    const chips = $('.ag-plp-head .ag-tchips');
    if (!wrap || !chips || $('#AgTypeList') || !I.typesLabel) return;
    const links = $$('a.ag-tchip', chips);
    if (links.length < 2) return;
    const sec = document.createElement('div');
    sec.id = 'AgTypeList';
    sec.className = 'ag-typelist';
    const h = document.createElement('h2');
    h.className = 'ag-typelist__title';
    h.textContent = I.typesLabel;
    const ul = document.createElement('ul');
    Array.from(chips.children).forEach((el) => {
      const li = document.createElement('li');
      if (el.matches('a.ag-tchip, a.ag-tchips__group')) {
        const c = el.cloneNode(true);
        c.className = `ag-typelist__link${el.classList.contains('is-on') ? ' is-on' : ''}${el.matches('.ag-tchips__group') ? ' ag-typelist__link--group' : ''}`;
        li.append(c);
      } else if (el.matches('.ag-tchips__group')) {
        li.className = 'ag-typelist__group';
        li.textContent = el.textContent;
      } else {
        return;
      }
      ul.append(li);
    });
    sec.append(h, ul);
    wrap.prepend(sec);
  }

  function openFacets() {
    // Short lists open (availability, price); long size lists stay folded.
    $$('#FacetFiltersForm details.facets__disclosure-vertical').forEach((d) => {
      if (d.dataset.agOpened) return;
      d.dataset.agOpened = '1';
      const n = $$('input[type="checkbox"]', d).length;
      if ((n && n <= 6) || $('price-range', d)) d.open = true;
    });
  }

  // ---------- show more ----------
  function nextLink(wrap) {
    if (!wrap) return null;
    const cur = Number(wrap.dataset.page || 1);
    return (
      $$('a[href]', wrap).find((a) => {
        try {
          return Number(new URL(a.href, location.origin).searchParams.get('page')) === cur + 1;
        } catch (e) {
          return false;
        }
      }) || null
    );
  }

  /** The list of cards: #product-grid itself on collections, the <ul> inside it on search results. */
  function gridList(root = document) {
    const host = root.getElementById('product-grid');
    if (!host) return null;
    return host.tagName === 'UL' ? host : $(':scope > ul.product-grid', host);
  }

  function setupMore() {
    const container = document.getElementById('ProductGridContainer');
    const grid = gridList();
    if (!container || !grid || !I.showMore) return;
    const wrap = $('.pagination-wrapper', container);
    let box = $('.ag-more', container);
    if (!box) {
      box = document.createElement('div');
      box.className = 'ag-more';
      box.innerHTML =
        '<p class="ag-more__shown"></p><button type="button" class="button button--secondary ag-more__btn"></button>';
      grid.after(box);
      $('.ag-more__btn', box).addEventListener('click', loadMore);
    }
    const main = readCount();
    const total = main === null ? null : main + extra.n;
    const shown = grid.children.length;
    $('.ag-more__shown', box).textContent = total !== null ? fill(I.shown, { a: fmt(shown), b: fmt(total) }) : '';
    const btn = $('.ag-more__btn', box);
    if (!btn.disabled) btn.textContent = I.showMore;
    btn.hidden = !nextLink(wrap) && extra.done;
    container.classList.add('ag-has-more');
    let empty = $('.ag-availability-empty', container);
    if (container.dataset.agAvailabilityFallback === 'true' && shown === 0 && !nextLink(wrap) && extra.done) {
      if (!empty) {
        empty = document.createElement('p');
        empty.className = 'ag-availability-empty title-wrapper center';
        empty.setAttribute('role', 'status');
        grid.after(empty);
      }
      empty.textContent = window.AgathaAvailability.emptyMessage;
    } else if (empty) empty.remove();
  }

  async function loadMore(e) {
    const btn = e.currentTarget;
    const container = document.getElementById('ProductGridContainer');
    const grid = gridList();
    const host = document.getElementById('product-grid');
    const wrap = $('.pagination-wrapper', container);
    const next = nextLink(wrap);
    if (!grid || !host) return;
    if (!next) {
      if (!extra.done) await loadExtra(btn, grid, host);
      return;
    }
    btn.disabled = true;
    btn.textContent = I.loading || I.showMore;
    try {
      let cursor = next;
      let currentWrap = wrap;
      let added = 0;
      let first = null;
      const batch = Number(container.dataset.agPageSize || 16);
      const fallback = container.dataset.agAvailabilityFallback === 'true';
      do {
        const u = new URL(cursor.href, location.origin);
        u.search = window.AgathaAvailability.params(u.searchParams, u.pathname).toString();
        u.searchParams.set('section_id', host.dataset.id);
        const res = await fetch(u.pathname + u.search);
        if (!res.ok) throw new Error(String(res.status));
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const list = gridList(doc);
        if (!list) throw new Error('product grid missing');
        // A page containing only unavailable products is valid; advance to its successor.
        if (!grid.isConnected) return; // filters changed during the request
        const items = $$(':scope > li', list);
        items.forEach((li) => {
          li.classList.remove('scroll-trigger', 'animate--slide-in');
          grid.append(document.adoptNode(li));
        });
        added += items.length;
        first ||= items[0];
        const newWrap = $('.pagination-wrapper', doc);
        if (currentWrap && newWrap) currentWrap.replaceWith(document.adoptNode(newWrap));
        else if (currentWrap) currentWrap.remove();
        currentWrap = $('.pagination-wrapper', container);
        cursor = nextLink(currentWrap);
      } while (cursor && (added === 0 || (fallback && added < batch)));
      const link = first && $('a', first);
      if (link && !e.agAutomatic) link.focus({ preventScroll: true });
    } catch (err) {
      location.assign(next.href);
      return;
    }
    btn.disabled = false;
    setupMore();
  }

  /** #102: append the next page of the other half of the age, skipping cards already shown. */
  async function loadExtra(btn, grid, host) {
    const state = extra;
    btn.disabled = true;
    btn.textContent = I.loading || I.showMore;
    try {
      const u = new URL(state.url, location.origin);
      u.searchParams.set('page', String(state.page + 1));
      u.searchParams.set('section_id', host.dataset.id);
      const res = await fetch(u.pathname + u.search);
      if (!res.ok) throw new Error(String(res.status));
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      if (state !== extra) return; // the filters changed meanwhile
      const list = gridList(doc);
      const items = list ? $$(':scope > li', list) : [];
      const path = (li) => {
        const a = $('a[href*="/products/"]', li);
        return a ? new URL(a.href, location.origin).pathname : '';
      };
      const seen = new Set($$(':scope > li', grid).map(path));
      items.forEach((li) => {
        if (seen.has(path(li))) return;
        li.classList.remove('scroll-trigger', 'animate--slide-in');
        grid.append(document.adoptNode(li));
      });
      state.page += 1;
      state.done = !items.length || !nextLink($('.pagination-wrapper', doc));
      const link = items[0] && $('a', items[0]);
      if (link && link.isConnected) link.focus({ preventScroll: true });
    } catch (err) {
      state.done = true;
      location.assign(state.url);
      return;
    }
    btn.disabled = false;
    setupMore();
  }

  // ---------- main ----------
  async function update() {
    const p = params();
    extra = noExtra();
    await loadAgeSets();
    const active = detectAge(p);
    const onPage = agesOnPage();
    // Integration: the header's language switch reads this to carry the age to the other language.
    const plp = $('[data-ag-plp]');
    if (plp) plp.setAttribute('data-ag-active-age', active.age || '');
    renderCount();
    relabelTaille();
    decorateFacetCounts();
    openFacets();
    collapseAgePills(p, active);
    keepFilters(p);
    setupMore();
    const container = document.getElementById('ProductGridContainer');
    const list = gridList();
    const more = container && $('.ag-more__btn', container);
    if (container?.dataset.agAvailabilityFallback === 'true' && list &&
        list.children.length < Number(container.dataset.agPageSize || 16) &&
        more && !more.hidden && !more.disabled) {
      await loadMore({ currentTarget: more, agAutomatic: true });
    }
    await renderRuler(p, active, onPage);
  }

  async function init() {
    if (!$('[data-ag-plp]')) return; // collection and search templates only (sections/agatha-collection-nav)
    I = readI18n();
    A = await whenAgatha();
    if (await convertAgeParam()) return;
    document.documentElement.classList.remove('ag-age-pending');
    // Another script stores {idx: 0} in the first history entry; Dawn's facets.js then reads
    // state.searchParams (undefined) on Back and shows the unfiltered collection. Give the entry
    // the parameters Dawn expects.
    try {
      const st = history.state;
      if (!st || (typeof st === 'object' && typeof st.searchParams !== 'string')) {
        history.replaceState(Object.assign({}, st || {}, { searchParams: location.search.slice(1) }), '');
      }
    } catch (e) {
      /* history not writable: keep Dawn's behaviour */
    }
    buildToolbar();
    buildTypeList();
    // Nothing to put in the sidebar (e.g. /collections/all, over 5,000 products: no filters): let the
    // grid take the full width.
    const vertical = $('.facets-vertical');
    if (vertical && !$('#FacetsWrapperDesktop .js-filter') && !$('#AgTypeList')) vertical.classList.add('ag-no-side');
    // Phones: the chip row scrolls sideways; bring the current type into view.
    const row = $('.ag-plp-head .ag-tchips');
    const cur = row && $('.ag-tchip.is-on', row);
    if (row && cur && row.scrollWidth > row.clientWidth) {
      row.scrollLeft += cur.getBoundingClientRect().left - row.getBoundingClientRect().left - 24;
    }
    const ruler = $('[data-ag-ruler]');
    if (ruler) {
      ruler.addEventListener('click', onRulerClick);
      rulerEdges(ruler);
    }
    const container = document.getElementById('ProductGridContainer');
    if (container) new MutationObserver(() => update()).observe(container, { childList: true });
    // facets.js re-renders the active pills outside the grid container.
    $$('.active-facets-desktop, .active-facets-mobile').forEach((box) => {
      new MutationObserver((list) => {
        const fresh = list.some((r) =>
          Array.from(r.addedNodes).some((n) => n.nodeType === 1 && !n.classList.contains('ag-age-pill'))
        );
        if (fresh) collapseAgePills(params(), detectAge(params()));
      }).observe(box, { childList: true });
    });
    window.addEventListener('popstate', () => setTimeout(update, 0));
    await update();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
